import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const HERO_IMAGE = "https://images.pexels.com/photos/4254166/pexels-photo-4254166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setFormError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/");
    } catch (err) {
      setErrors(getFieldErrors(err));
      setFormError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden flex-1 lg:block">
        <img src={HERO_IMAGE} alt="Solar EPC construction site" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="absolute bottom-0 left-0 p-12">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.3em] text-gold-400">SA Renew Private Limited · Ground to Grid</p>
          <h1 className="font-heading max-w-lg text-5xl font-bold leading-[1.05] tracking-tight text-white xl:text-6xl">
            Track every <span className="text-gold-400">watt.</span><br />Deliver every phase.
          </h1>
          <p className="mt-4 max-w-md leading-relaxed text-slate-300">
            Engineering, procurement and site progress — one command center for your entire EPC portfolio.
          </p>
        </div>
      </div>

      <div className="relative flex w-full flex-col justify-center border-l border-border bg-card px-8 sm:px-14 lg:w-[480px]">
        <ThemeToggle className="absolute top-6 right-6" />
        <div className="mb-10"><BrandMark /></div>

        <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground">Sign in</h2>
        <p className="mb-8 text-sm text-muted-foreground">Access your project command center</p>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
          <div>
            <Label htmlFor="email" className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Email</Label>
            <Input
              id="email"
              type="email"
              required
              data-testid="login-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@company.com"
              className="mt-2"
            />
            <FieldError error={errors.email} />
          </div>
          <div>
            <Label htmlFor="password" className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Password</Label>
            <Input
              id="password"
              type="password"
              required
              data-testid="login-password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              className="mt-2"
            />
            <FieldError error={errors.password} />
          </div>

          {formError && <p className="text-xs text-red-600 dark:text-red-400" data-testid="login-error-message">{formError}</p>}

          <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full uppercase tracking-[0.15em]">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Sign In
          </Button>
        </form>
      </div>
    </div>
  );
}
